import React, { useState, useEffect, useRef, FunctionComponent, ReactElement } from 'react';
import { Helmet } from 'react-helmet';
import useGlobal from 'hooks/useGlobal';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import TimesIcon from 'icon/times';
import Portal from './portal';

import { active as activeStyles, inactive } from './styles';
import * as s from './modal.module.scss';
import useWindowWidth from '@/hooks/useWindowWidth';

interface IModalProps {
  children: ReactElement<unknown>;
  closeModal?: React.Dispatch<void>;
  noCancel?: boolean;
  big?: boolean;
  bigSearch?: boolean;
  cancelSearch?: boolean;
  nolineSearch?: boolean;
  id: string;
  className?: string | null;
  wrapperClass?: string | null;
  cancelClass?: string | null;
  video?: boolean | null;
  thick?: boolean | null;
  closeButtonClass?: string;
  inputValue?: string | null;
}

const Modal: FunctionComponent<IModalProps> = ({
  inputValue,
  className,
  wrapperClass,
  cancelClass,
  children,
  closeModal,
  noCancel,
  big,
  bigSearch,
  nolineSearch,
  cancelSearch,
  id,
  thick,
  video,
  closeButtonClass,
}) => {
  const global = useGlobal();
  const [active, setActive] = useState(false);
  const modal = useRef<HTMLDivElement | null>(null);
  const handleClose = () => {
    if (active) {
      setActive(false);
      setTimeout(() => {
        global?.emit.update({
          modal: false,
        });
        if (closeModal) {
          closeModal();
        }
      }, 500);
    }
  };

  const { windowWidth } = useWindowWidth();
  useEffect(() => {
    const isMobile = windowWidth < 1200;
    setActive(true);
    if (modal?.current && isMobile) {
      modal.current.focus();
    }
    global?.emit.update({
      modal: true,
    });
  }, []);
  // classes
  const thickClass = thick ? s.thick : '';
  const heightClass = !!inputValue ? s.heightChange : '';
  // const videoClass = video ? s.video : '';
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const focusableElementString =
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
    const focusableElements = modal.current?.querySelectorAll(focusableElementString);
    const firstTabStop = focusableElements?.[0] as HTMLElement | null;
    const lastTabStop = focusableElements?.[focusableElements.length - 1] as HTMLElement | null;
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstTabStop) {
          e.preventDefault();
          lastTabStop?.focus();
        }
      } else if (document.activeElement === lastTabStop) {
        e.preventDefault();
        firstTabStop?.focus();
      }
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <>
      <Helmet>
        <style>{id && inactive}</style>
      </Helmet>
      {active && (
        <Helmet>
          <style>{id && activeStyles}</style>
        </Helmet>
      )}
      <Portal id={id}>
        <button
          className={`${s.overlay}`}
          onClick={noCancel ? () => null : handleClose}
          onKeyDown={(e) => {
            if (!noCancel && (e.key === 'Enter' || e.key === ' ')) {
              handleClose();
            }
          }}
          tabIndex={0}
        ></button>
        <div
          className={`${s.wrapper} ${active ? s.active : ''} ${wrapperClass || ''}`}
          ref={modal}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <button className={`${s.overlay}`} onClick={noCancel ? () => null : handleClose} />
          <div
            className={`${heightClass} ${s.modal} ${big ? s.big : ''} ${bigSearch ? s.bigSearch : ''} ${
              className || ''
            }  ${thickClass} `}
          >
            {!video && <div className={`${nolineSearch ? s.nolineSearch : s.line}`} />}
            {React.cloneElement(children, {
              closeModal: handleClose,
            })}
            {!noCancel && !cancelSearch && (
              <button
                type="button"
                onClick={handleClose}
                title="Close Modal"
                className={`${s.close} ${closeButtonClass || ''}`}
              >
                <TimesIcon />
              </button>
            )}
            {cancelSearch && (
              <button
                type="button"
                onClick={handleClose}
                title="Close Modal"
                className={`${s.closeCancelSearch} ${closeButtonClass || ''}`}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </Portal>
    </>
  );
};

export default Modal;
